import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link2, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  buildWhatsAppUrl,
  parseWhatsApp,
} from '@/features/noticias/cta-url'

type Mode = 'url' | 'whatsapp'

interface CtaUrlFieldProps {
  value: string
  onChange: (next: string) => void
  error?: string
}

/**
 * Editor de la URL del CTA. Dos modos:
 * - URL libre (valida server-side via schemes permitidos).
 * - WhatsApp: telefono + mensaje, construye la URL `https://wa.me/<digitos>?text=...`.
 *
 * El componente es controlado: el unico estado externo es el string `value`;
 * el resto (modo, telefono, mensaje) es UI local. Al cambiar a modo URL se
 * preserva la URL construida para que el admin la pueda copiar/editar.
 */
export function CtaUrlField({ value, onChange, error }: CtaUrlFieldProps): ReactNode {
  // Modo inicial deriva de la URL existente. Hooks de mount-only para no
  // pisar al admin mientras tipea.
  const initialParsed = useRef(parseWhatsApp(value))
  const [mode, setMode] = useState<Mode>(initialParsed.current ? 'whatsapp' : 'url')
  const [phone, setPhone] = useState(initialParsed.current?.phone ?? '')
  const [message, setMessage] = useState(initialParsed.current?.message ?? '')

  // Cuando cambia phone/message en modo WhatsApp, sincronizamos al form
  // construyendo la URL final. En modo URL no tocamos el value externo aca:
  // el Input lo maneja directo via su propio onChange.
  useEffect(() => {
    if (mode !== 'whatsapp') return
    onChange(buildWhatsAppUrl(phone, message))
    // onChange es estable (RHF) — lo dejamos fuera del array para evitar loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phone, message])

  function switchMode(next: Mode) {
    if (next === mode) return
    if (next === 'whatsapp') {
      // Si la URL actual ya es de WhatsApp, parseamos para precargar.
      const parsed = parseWhatsApp(value)
      setPhone(parsed?.phone ?? phone)
      setMessage(parsed?.message ?? message)
    }
    // Al pasar a URL no limpiamos value: el admin puede querer ajustarlo.
    setMode(next)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={mode === 'url' ? 'ctaUrl' : 'ctaWhatsappPhone'}>
          URL del CTA
        </Label>
        <div className="inline-flex rounded-md border bg-background p-0.5">
          <ToggleButton
            active={mode === 'url'}
            onClick={() => switchMode('url')}
            icon={<Link2 className="h-3.5 w-3.5" />}
            label="URL"
          />
          <ToggleButton
            active={mode === 'whatsapp'}
            onClick={() => switchMode('whatsapp')}
            icon={<MessageCircle className="h-3.5 w-3.5" />}
            label="WhatsApp"
          />
        </div>
      </div>

      {mode === 'url' ? (
        <>
          <Input
            id="ctaUrl"
            placeholder="https://..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Schemes aceptados: https://, wa.me/, tel:, mailto:
            </p>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-[180px_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ctaWhatsappPhone" className="text-xs">
              Telefono (con codigo de pais)
            </Label>
            <Input
              id="ctaWhatsappPhone"
              inputMode="tel"
              placeholder="+598 99 123 456"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ctaWhatsappMessage" className="text-xs">
              Mensaje pre-cargado (opcional)
            </Label>
            <Textarea
              id="ctaWhatsappMessage"
              rows={2}
              placeholder="Hola, quiero info sobre..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : (
              <p
                className={cn(
                  'text-xs text-muted-foreground',
                  !phone && 'italic',
                )}
                title={value || ''}
              >
                {phone
                  ? `URL final: ${value}`
                  : 'Ingresa el teléfono para generar el link.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface ToggleButtonProps {
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
}

function ToggleButton({ active, onClick, icon, label }: ToggleButtonProps): ReactNode {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        'h-7 gap-1.5 px-2.5 text-xs',
        active && 'bg-muted text-foreground shadow-sm',
      )}
      onClick={onClick}
    >
      {icon}
      {label}
    </Button>
  )
}
